import { CustomerService } from '@/pages/service/customer.service';
import { ProductService } from '@/pages/service/product.service';
import { Customer, Representative } from '@/types/customer';
import { Product } from '@/types/product';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    inject,
    signal,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import {
    BranchTypeQuery,
    DCLCorrelationGraphQLSchemaActions,
    DCLCorrelationTypeQuery,
    DCLGarageUseType,
    DCLGarageUseTypeQuery,
    DCLParkingCarWashUseType,
    DCLParkingCarWashUseTypeQuery,
    DCLRentalUseType,
    DCLRentalUseTypeQuery,
    DCLResponseTypeQuery,
    DigitalClientCardGraphQLSchema,
    DigitalClientCardGraphQLSchemaActions,
    DigitalClientCardGraphQLSchemaSelectors,
    DigitalClientCardType,
    DigitalClientCardTypeFormGroup,
    DigitalClientCardTypeQuery,
    InvoiceType,
    InvoiceTypeQuery,
    ProviderResponseErrorTypeQuery,
    ProviderResponseTypeQuery,
    ResponseTypeErrorTypeQuery,
    UsersActionsTypeQuery
} from '@shared/zigra-schemas/zigra-schema.autogenerated.models';
import {
    ConfirmationService,
    FilterMatchMode,
    FilterOperator,
    MenuItem,
    MessageService
} from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { RatingModule } from 'primeng/rating';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { finalize, map, Subject, takeUntil } from 'rxjs';
import {
    FetchQueryFilterGroup,
    QueryFilter,
    QueryFilterGroup,
    ZAGGridFilterCondition,
    ZAGGridFilters
} from 'zigra-ng-framework';
import { DclCardComponent } from './components/dcl-card/dcl-card.component';
import { DCLCardOptions } from './entities/dcl-card.options';
import { DclCardFooterComponent } from './components/dcl-card-footer/dcl-card-footer.component';
import { ZDatePipe } from '../../../shared/pipes/ZDate.pipe';
import { InvoiceCardFooterComponent } from '@portal/invoicing/invoices-cards/components/footer/invoice-card-footer/invoice-card-footer.component';
import { InvoiceCardComponent } from '@portal/invoicing/invoices-cards/components/invoice-card/invoice-card.component';
import { InvoiceOptions } from '@portal/invoicing/invoices-cards/entities/invoice-card.options';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ZSplitButtonComponent } from '../../../shared/components/form-components/z-split-button/z-split-button.component';
import { DclCardService } from '@shared/services/dcl-card/dcl-card.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { LoaderService } from '@shared/services/loader.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ZInvoiceSeriesInfoComponent } from '@portal/invoicing/invoices-cards/components/z-invoice-series-info/z-invoice-series-info.component';
import { ZPLateFormatPipe } from '../../../shared/pipes/plate-format.pipe';
import { DateTime } from 'luxon';

interface expandedRows {
    [key: string]: boolean;
}

// export const dddd: DigitalClientCardType = {
//     DCLNewId: undefined,
//     DCLUpdateId: undefined,
//     ClientServiceType: 1,
//     CreationDateTime: new Date(),
//     BranchAADE: 0,

//     ContinuousService: false,
//     FromAgreedPeriodDate: undefined,
//     ToAgreedPeriodDate: undefined,
//     Periodicity: undefined,

//     RecurringService: false,
//     CustomerVatNumber: undefined,
//     CustomerCountry: undefined,

//     MixedService: false,

//     Garage: {
//         VehicleRegistrationNumber: ''
//     } as DCLGarageUseType,
//     ParkingUse: {
//         VehicleRegistrationNumber: ''
//     } as DCLParkingCarWashUseType,
//     RentalUse: {
//         VehicleRegistrationNumber: ''
//     } as DCLRentalUseType,
//     Comments: undefined,
//     CorrelatedDclId: undefined,

//     EntryCompletion: undefined,
//     NonIssueInvoice: undefined,
//     Amount: undefined,
//     CompletionDateTime: undefined,
//     IsDiffVehReturnLocation: undefined,
//     VehicleReturnLocation: undefined,
//     ProvidedServiceCategory: undefined,
//     ProvidedServiceCategoryOther: undefined,
//     InvoiceKind: undefined,
//     OffSiteProvidedService: undefined,
//     ExitDateTime: undefined,
//     EntityVatNumber: undefined,
//     CooperatingVatNumber: undefined,
//     OtherBranchAADE: undefined,
//     InvoiceCounterparty: undefined,
//     InvoiceCounterpartyCountry: undefined
// };

export const digitalClientCardsQuery: DigitalClientCardTypeQuery = {
    ...DigitalClientCardTypeQuery.GQLQuery(),
    UsersActions: UsersActionsTypeQuery.GQLQuery(),
    Responses: DCLResponseTypeQuery.GQLQuery({
        Errors: ResponseTypeErrorTypeQuery.GQLQuery({
            Error: ProviderResponseErrorTypeQuery.GQLQuery()
        }),
        DCLCorrelations: DCLCorrelationTypeQuery.GQLQuery(),
        UsersActions: UsersActionsTypeQuery.GQLQuery()
    }),
    DCLNewId: true,
    DCLUpdateId: true,
    ClientServiceType: true,
    CreationDateTime: true, // Ημερομηνία και ώρα δημιουργίας εγγραφής στο Ψηφιακό Πελατολόγιο
    BranchAADE: true, //  Εγκατάσταση (Υποκατάστημα)
    Garage: DCLGarageUseTypeQuery.GQLQuery(),
    ParkingUse: DCLParkingCarWashUseTypeQuery.GQLQuery(),
    RentalUse: DCLRentalUseTypeQuery.GQLQuery(),

    ContinuousService: true,
    FromAgreedPeriodDate: true,
    ToAgreedPeriodDate: true,

    RecurringService: true,
    CustomerVatNumber: true,
    CustomerCountry: true,

    MixedService: true,

    Comments: true,

    CorrelatedDclId: true,

    // Update Client
    EntryCompletion: true,
    NonIssueInvoice: true,
    Amount: true,
    CompletionDateTime: true,
    IsDiffVehReturnLocation: true,
    VehicleReturnLocation: true,
    ProvidedServiceCategory: true,
    ProvidedServiceCategoryOther: true,
    InvoiceKind: true,
    OffSiteProvidedService: true, // // 16?. Παροχή Υπηρεσίων Εκτός Εγκατάστασης (OffSiteProvidedServiceType) (null = Χωρίς Μετακίνηση, 1 = Μετακίνηση σε συνεργαζόμενη οντότητα, 2 = Μετακίνηση σε εγκατάσταση εντός της ίδιας οντοτήτας)
    ExitDateTime: true,
    EntityVatNumber: true,
    CooperatingVatNumber: true, // 17?. Α.Φ.Μ Συνεργαζόμενης Οντοτητας (για το OffSiteProvidedServiceType = 2)
    OtherBranchAADE: true, // 18?. Άλλος Αρ. Εγκατάστασης εντός της ίδιας οντότητας (αλλο Branch) (για το OffSiteProvidedServiceType = 3)
    InvoiceCounterparty: true,
    InvoiceCounterpartyCountry: true,
    BranchId: true,
    BranchOtherId: true,
    Branch: BranchTypeQuery.GQLQuery(),
    BranchOther: BranchTypeQuery.GQLQuery(),
    // InvoiceId: true,
    // Invoice: InvoiceTypeQuery.GQLQuery(),
    Invoices: InvoiceTypeQuery.GQLQuery({
        Response: ProviderResponseTypeQuery.GQLQuery({
            Errors: ResponseTypeErrorTypeQuery.GQLQuery({
                Error: ProviderResponseErrorTypeQuery.GQLQuery()
            })
        }),
        DCLCorrelationResponse: DCLResponseTypeQuery.GQLQuery({
            Errors: ResponseTypeErrorTypeQuery.GQLQuery({
                Error: ProviderResponseErrorTypeQuery.GQLQuery()
            })
        })
    })

    // 1. Παρεχόμενη Υπηρεσία (DropDown) // Απαξ, Επαναλαμβανόμενη, Διαρκείς.
    // 2?. Εγκατάσταση (Υποκατάστημα)
    // 3. Αριθμός Κυκλοφορίας Οχήματος - Αριθμός Κυκλοφορίας Αλλοδαπής
    // 4. Ημερομηνία Εισόδου Οχήματος -  Ώρα Εισόδου Οχήματος
    // 5?. Εργοστάσιο Οχήματος - Κατηγορία Οχήματος
    // 6. Παρεχομενη Υπηρεσία (ProvidedCategoryServiceType)
    // 7?. Ημερομηνία ΕΞόδου Οχήματος - Ώρα Εξόδου Οχήματος
    // 8?. Συσχέτιση με άλλη Δήλωση Ψηφιακού Πελατολογίου?
    // 9?. Συσχετιζόμενος Α/Α Ψηφιακού Πελατολογίου
    // 10?. Μοναδικός Αριθμός Ψηφιακού Πελατολογίου (μας τον επιστρέφει η Α.Α.Δ.Ε)
    // 11?. Ειδός Παραστατικού (1 = ΑΛΠ/ΑΠΥ, 2 = ΤΙΜΟΛΟΓΙΟ, 3 = ΑΛΠ/ΑΠΥ-ΦΗΜ)
    // 12?. Α.Φ.Μ Αντισυμβαλόμενου (Εμφανίζεται αν επιλεγεί ΤΙΜΟΛΟΓΙΟ)
    // 13?. Συμφωνηθέν Ποσό (amount)
    // 14?. Παρατηρήσεις (comments)
    // 15. Οριστικοποιήμενη Εγγραφή - Κατάσταση (Η οριστικοποίηση θα γίνει με την έξοδο του οχήματος)
    // 16?. Παροχή Υπηρεσίων Εκτός Εγκατάστασης (OffSiteProvidedServiceType) (null = Χωρίς Μετακίνηση, 1 = Μετακίνηση σε συνεργαζόμενη οντότητα, 2 = Μετακίνηση σε εγκατάσταση εντός της ίδιας οντοτήτας)
    // 17?. Α.Φ.Μ Συνεργαζόμενης Οντοτητας (για το OffSiteProvidedServiceType = 2)
    // 18?. Άλλος Αρ. Εγκατάστασης εντός της ίδιας οντότητας (αλλο Branch) (για το OffSiteProvidedServiceType = 3)
    //
    // 19?. Παραστατικό - Μ.Α.Ρ.Κ (με την καταχώρηση του ΜΑΡΚ να κάνει Query Στην ΑΑΔΕ να φέρει τα υπόλοιπα στοιχεία)
    // Παραστατικό - Α/Α Παραστατικού
    // Παραστατικό - Σειρά Παραστατικού
    // Παραστατικό - Ημερομηνία Έκδοσης (μόνο Ημέρα, όχι ώρα)
    // Παραστατικό - Συνολική Αξία
    // Παραστατικό - Τρόπος Πληρωμής
} as DigitalClientCardTypeQuery;

// export const queryForBatch: InvoiceTypeQuery = {
//   ...InvoiceTypeQuery.GQLQuery(),
//   CounterPart: InvoicePartyTypeQuery.GQLQuery({
//     ...InvoicePartyTypeQuery.GQLQuery(),
//     Address: AddressTypeQuery.GQLQuery(),
//     Customer: CustomerTypeQuery.GQLQuery({
//       ...CustomerTypeQuery.GQLQuery(),
//       FirstName: FirstNameTypeQuery.GQLQuery(),
//     }),
//     Supplier: SupplierTypeQuery.GQLQuery({
//       ...CustomerTypeQuery.GQLQuery(),
//       FirstName: FirstNameTypeQuery.GQLQuery(),
//     }),
//   }),
//   Issuer: InvoicePartyTypeQuery.GQLQuery({
//     ...InvoicePartyTypeQuery.GQLQuery(),
//     Address: AddressTypeQuery.GQLQuery(),
//     Customer: CustomerTypeQuery.GQLQuery({
//       ...CustomerTypeQuery.GQLQuery(),
//       FirstName: FirstNameTypeQuery.GQLQuery(),
//     }),
//     Supplier: SupplierTypeQuery.GQLQuery({
//       ...CustomerTypeQuery.GQLQuery(),
//       FirstName: FirstNameTypeQuery.GQLQuery(),
//     }),
//   }),
//   Courier: CourierTypeQuery.GQLQuery(),
//   DeliveryNodeHeader: InvoiceDeliveryNoteHeaderTypeQuery.GQLQuery(),
//   Details: InvoiceDetailTypeQuery.GQLQuery(),
//   Incomes: InvoiceIncomeClassificationTypeQuery.GQLQuery(),
//   Notes: InvoiceNoteTypeQuery.GQLQuery(),
//   Response: ProviderResponseTypeQuery.GQLQuery({
//     Errors: ResponseTypeErrorTypeQuery.GQLQuery({
//       Error: ProviderResponseErrorTypeQuery.GQLQuery(),
//     }),
//   }),
//   //Responses: ProviderResponseTypeQuery.GQLQuery(),
//   SubjectsInfos: InvoiceEntitySubjectInfoTypeQuery.GQLQuery(),
//   Summaries: InvoiceSummaryTypeQuery.GQLQuery(),
//   Taxes: InvoiceTaxTypeQuery.GQLQuery(),
//   Payments: InvoicePaymentTypeQuery.GQLQuery(),
// };

@Component({
    selector: 'zion-dcl-cards',
    imports: [
        TableModule,
        MultiSelectModule,
        SelectModule,
        InputIconModule,
        TagModule,
        InputTextModule,
        SliderModule,
        ProgressBarModule,
        ToggleButtonModule,
        ToastModule,
        CommonModule,
        FormsModule,
        ButtonModule,
        RatingModule,
        RippleModule,
        IconFieldModule,
        ZDatePipe,
        SplitButtonModule,
        ConfirmDialog,
        ZInvoiceSeriesInfoComponent,
        ZPLateFormatPipe
    ],
    providers: [
        ConfirmationService,
        MessageService,
        CustomerService,
        ProductService
    ],
    templateUrl: './dcl-cards.component.html',
    styleUrl: './dcl-cards.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class DclCardsComponent {
    @HostBinding('class') class: string = 'w-full';

    @ViewChild('footer') footer: any;
    @ViewChild('dt1') dt1?: Table;

    dclCards = signal<DigitalClientCardType[] | undefined>(undefined);
    store = inject(Store);
    dialogService = inject(DialogService);
    dclCardService = inject(DclCardService);
    confirmationService = inject(ConfirmationService);
    loaderService = inject(LoaderService);

    event?: TableLazyLoadEvent;
    totalRecords: number = 0;
    loading = signal<boolean | undefined>(undefined);
    unSubscribe = new Subject<boolean>();
    // selectedDCLCard?: DigitalClientCardType;

    //     1 = ΑΛΠ/ΑΠΥ
    //     11.1 : Παραστατικά Λιανικής -> ΑΛΠ
    //     11.2 : Παραστατικά Λιανικής -> ΑΠΥ
    //     11.4 : Παραστατικά Λιανικής ->  Πιστωτικό Στοιχ. Λιανικής
    // 2 = ΤΙΜΟΛΟΓΙΟ
    //     1.1 : Τιμολόγιο Πώλησης -> Τιμολόγιο Πώλησης
    //     2.1 : Τιμολόγιο Παροχής Υπηρεσιών -> Τιμολόγιο Παροχής
    //     5.2 : Πιστωτικό Τιμολόγιο -> Πιστωτικό Τιμολόγιο / Μη Συσχετιζόμενο

    splitButtonItems: MenuItem[] = [
        {
            label: 'Απόδειξη (Α.Π.Υ)',
            title: 'Απόδειξη Παροχής Υπηρεσιών (Α.Π.Υ)',
            code: '11.2'
            // command: () => {
            //     alert('11.2');
            //     if (this.selectedDCLCard)
            //         this.newInvoice(this.selectedDCLCard, '11.2');

            //     this.selectedMenuItem = this.splitButtonItems[0];
            // }
        },
        { separator: true },
        {
            label: 'Τιμολόγιο (Τ.Π.Υ)',
            title: 'Τιμολόγιο Παροχής Υπηρεσιών (Τ.Π.Υ)',
            code: '2.1'
            // command: () => {
            //     alert('2.1');
            //     if (this.selectedDCLCard)
            //         this.newInvoice(this.selectedDCLCard, '2.1');
            //     this.selectedMenuItem = this.splitButtonItems[1];
            // }
        }
    ];
    selectedMenuItem: MenuItem = this.splitButtonItems[0];

    dt = DateTime;

    // customers1: Customer[] = [];
    // customers2: Customer[] = [];
    // customers3: Customer[] = [];
    // representatives: Representative[] = [];
    // statuses: any[] = [];
    // products: Product[] = [];
    // rowGroupMetadata: any;
    // expandedRows: expandedRows = {};
    // activityValues: number[] = [0, 100];
    // balanceFrozen: boolean = false;
    // loading: boolean = true;
    // @ViewChild('filter') filter!: ElementRef;
    constructor() {
        this.dclCardService.refreshData
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this.refreshTable();
            });
    }

    // ngOnInit() {
    //     this.customerService.getCustomersLarge().then((customers) => {
    //         this.customers1 = customers;
    //         this.loading = false;
    //         // @ts-ignore
    //         // this.customers1.forEach(
    //         //     (customer) => (customer.date = new Date(customer.date))
    //         // );
    //     });
    //     this.customerService
    //         .getCustomersMedium()
    //         .then((customers) => (this.customers2 = customers));
    //     this.customerService
    //         .getCustomersLarge()
    //         .then((customers) => (this.customers3 = customers));
    //     //this.productService.getProductsWithOrdersSmall().then((data) => (this.products = data));
    //     this.representatives = [
    //         { name: 'Amy Elsner', image: 'amyelsner.png' },
    //         { name: 'Anna Fali', image: 'annafali.png' },
    //         { name: 'Asiya Javayant', image: 'asiyajavayant.png' },
    //         { name: 'Bernardo Dominic', image: 'bernardodominic.png' },
    //         { name: 'Elwin Sharvill', image: 'elwinsharvill.png' },
    //         { name: 'Ioni Bowcher', image: 'ionibowcher.png' },
    //         { name: 'Ivan Magalhaes', image: 'ivanmagalhaes.png' },
    //         { name: 'Onyama Limba', image: 'onyamalimba.png' },
    //         { name: 'Stephen Shaw', image: 'stephenshaw.png' },
    //         { name: 'XuXue Feng', image: 'xuxuefeng.png' }
    //     ];
    //     this.statuses = [
    //         { label: 'Unqualified', value: 'unqualified' },
    //         { label: 'Qualified', value: 'qualified' },
    //         { label: 'New', value: 'new' },
    //         { label: 'Negotiation', value: 'negotiation' },
    //         { label: 'Renewal', value: 'renewal' },
    //         { label: 'Proposal', value: 'proposal' }
    //     ];
    // }
    // onSort() {
    //     this.updateRowGroupMetaData();
    // }
    // updateRowGroupMetaData() {
    //     this.rowGroupMetadata = {};
    //     if (this.customers3) {
    //         for (let i = 0; i < this.customers3.length; i++) {
    //             const rowData = this.customers3[i];
    //             const representativeName = rowData?.representative?.name || '';
    //             if (i === 0) {
    //                 this.rowGroupMetadata[representativeName] = {
    //                     index: 0,
    //                     size: 1
    //                 };
    //             } else {
    //                 const previousRowData = this.customers3[i - 1];
    //                 const previousRowGroup =
    //                     previousRowData?.representative?.name;
    //                 if (representativeName === previousRowGroup) {
    //                     this.rowGroupMetadata[representativeName].size++;
    //                 } else {
    //                     this.rowGroupMetadata[representativeName] = {
    //                         index: i,
    //                         size: 1
    //                     };
    //                 }
    //             }
    //         }
    //     }
    // }
    // formatCurrency(value: number) {
    //     return value.toLocaleString('en-US', {
    //         style: 'currency',
    //         currency: 'USD'
    //     });
    // }
    // onGlobalFilter(table: Table, event: Event) {
    //     table.filterGlobal(
    //         (event.target as HTMLInputElement).value,
    //         'contains'
    //     );
    // }
    // clear(table: Table) {
    //     table.clear();
    //     this.filter.nativeElement.value = '';
    // }
    // getSeverity(status: string) {
    //     switch (status) {
    //         case 'qualified':
    //         case 'instock':
    //         case 'INSTOCK':
    //         case 'DELIVERED':
    //         case 'delivered':
    //             return 'success';
    //         case 'negotiation':
    //         case 'lowstock':
    //         case 'LOWSTOCK':
    //         case 'PENDING':
    //         case 'pending':
    //             return 'warn';
    //         case 'unqualified':
    //         case 'outofstock':
    //         case 'OUTOFSTOCK':
    //         case 'CANCELLED':
    //         case 'cancelled':
    //             return 'danger';
    //         default:
    //             return 'info';
    //     }
    // }
    // calculateCustomerTotal(name: string) {
    //     let total = 0;
    //     if (this.customers2) {
    //         for (let customer of this.customers2) {
    //             if (customer.representative?.name === name) {
    //                 total++;
    //             }
    //         }
    //     }
    //     return total;
    // }
    // expandAll() {
    //     this.expandedRows = this.products.reduce(
    //         (acc, p) => {
    //             if (p.id) {
    //                 acc[p.id] = true;
    //             }
    //             return acc;
    //         },
    //         {} as { [key: string]: boolean }
    //     );
    // }
    // collapseAll() {
    //     this.expandedRows = {};
    // }

    onLazyLoad(event: TableLazyLoadEvent) {
        this.event = event;
        this.loading.set(true);
        let fetchQueryParameters =
            QueryFilter.FetchQueryParamsFromPrimeNGTable(event);

        this.store
            .dispatch([
                new DigitalClientCardGraphQLSchemaActions.FetchAndCount(
                    digitalClientCardsQuery,
                    {
                        ...fetchQueryParameters, //...QueryFilterGroup.JoinWithAndOperator(groups), //...QueryFilter.FetchQueryParamsFromAGGridParams(params),
                        SortQuery: [
                            {
                                Identifier: 'UsersActions.CreatedDate',
                                Order: 'desc'
                            }
                        ]
                    },
                    '',
                    ''
                ),
                new DigitalClientCardGraphQLSchemaActions.Fetch(
                    digitalClientCardsQuery,
                    {
                        ...fetchQueryParameters, //...QueryFilterGroup.JoinWithAndOperator(groups), //...QueryFilter.FetchQueryParamsFromAGGridParams(params),
                        SortQuery: [
                            {
                                Identifier: 'UsersActions.CreatedDate',
                                Order: 'desc'
                            }
                        ]
                    },
                    '',
                    ''
                )
            ])
            .pipe(
                finalize(() => this.loading.set(undefined))
                // map(
                //     () =>
                //         this.store.selectSnapshot(
                //             DigitalClientCardGraphQLSchemaSelectors.FetchSelector(
                //                 '',
                //                 ''
                //             )
                //         )?.data?.Fetch ?? []
                // )
            )
            .subscribe(() => {
                this.totalRecords =
                    this.store.selectSnapshot(
                        DigitalClientCardGraphQLSchemaSelectors.FetchAndCountSelector(
                            '',
                            ''
                        )
                    )?.data?.FetchAndCount ?? 0;

                const dclCards =
                    this.store.selectSnapshot(
                        DigitalClientCardGraphQLSchemaSelectors.FetchSelector(
                            '',
                            ''
                        )
                    )?.data?.Fetch ?? [];
                this.dclCards.set(dclCards);
                dclCards[0].Invoices;
            });
    }

    newDCL() {
        const dialogRef = this.dialogService.open<DclCardComponent>(
            DclCardComponent,
            {
                header: 'Νέα Εγγραφή Ψηφιακού Πελατολογίου',
                height: '800px',
                width: '750px',
                modal: true,
                resizable: true,
                draggable: true,
                closable: true,
                focusOnShow: false,
                templates: {
                    footer: DclCardFooterComponent
                },
                data: {
                    mode: 'new',
                    onDCLCardSave: (options: DCLCardOptions) => {
                        dialogRef.close();
                        //if (this.event) this.onLazyLoad(this.event);
                    }
                }
            } as DynamicDialogConfig<DCLCardOptions>
        );

        dialogRef.onClose?.pipe(takeUntil(this.unSubscribe)).subscribe(() => {
            this.refreshTable();
        });
    }

    onRowDblClick(
        digitalClientCardType: DigitalClientCardType,
        rowIndex: number
    ) {
        const dialogRef = this.dialogService.open<DclCardComponent>(
            DclCardComponent,
            {
                header: 'Μεταβολή Εγγραφής Ψηφιακού Πελατολογίου',
                height: '800px',
                width: '750px',
                modal: true,
                resizable: true,
                draggable: true,
                closable: true,
                closeOnEscape: false,

                appendTo: 'body',

                templates: {
                    footer: DclCardFooterComponent
                },
                data: {
                    dclId: digitalClientCardType?._id,
                    mode: 'edit',
                    onDCLCardSave: (options: DCLCardOptions) => {
                        dialogRef.close();
                        //if (this.event) this.onLazyLoad(this.event);
                    }
                }
            } as DynamicDialogConfig<DCLCardOptions>
        );

        dialogRef.onClose?.pipe(takeUntil(this.unSubscribe)).subscribe(() => {
            this.refreshTable();
        });
    }

    refreshTable(resetTotalRecords?: boolean) {
        if (resetTotalRecords == true) this.totalRecords = 0;
        if (this.event) this.onLazyLoad(this.event);
    }

    // public FiltersFromPrimeNGTable(
    //     tableLazyLoadEvent: TableLazyLoadEvent
    // ): FetchQueryFilterGroup<any> | undefined {
    //     // const filtersMap: Map<string, string> = new Map<string, string>();
    //     // filtersMap.set('text-contains', 'FieldContainsString');

    //     const filters = Object.keys(tableLazyLoadEvent.filters ?? {}).map(
    //         (filterKey) => {
    //             if (tableLazyLoadEvent.filters?.[filterKey] != undefined) {
    //             }

    //             const filtersMetaData = Array.isArray(
    //                 tableLazyLoadEvent.filters?.[filterKey]
    //             )
    //                 ? tableLazyLoadEvent.filters?.[filterKey]
    //                 : [tableLazyLoadEvent.filters?.[filterKey]];

    //             //filterMetaData[0].matchMode
    //             // filterMetaData[0].operator
    //             // filterMetaData[0].value

    //             const conditions = filtersMetaData
    //                 ?.filter(
    //                     (metaData) =>
    //                         metaData?.value != undefined &&
    //                         metaData?.value != null
    //                 )
    //                 ?.map((filterMetaData) => {
    //                     let filterGroup = QueryFilterGroup.FieldIsEqualToString(
    //                         filterKey.split('.'),
    //                         filterMetaData?.value
    //                     );

    //                     //FilterOperator.AND
    //                     switch (filterMetaData?.matchMode) {
    //                         case FilterMatchMode.CONTAINS:
    //                             filterGroup =
    //                                 QueryFilterGroup.FieldContainsString(
    //                                     filterKey.split('.'),
    //                                     filterMetaData.value
    //                                 );
    //                             break;
    //                     }

    //                     switch (filterMetaData?.operator) {
    //                         case FilterOperator.AND:
    //                             break;
    //                         case FilterOperator.OR:
    //                             break;
    //                     }

    //                     // switch (filtersMap.get(`${filterMetaData.filterType}-${filterMetaData.type}`)) {
    //                     //   case 'FieldContainsString':
    //                     //     filterGroup = QueryFilterGroup.FieldContainsString(
    //                     //       filterKey.split('.'),
    //                     //       filterMetaData.filter
    //                     //     );
    //                     //     break;
    //                     // }
    //                     return filterGroup;
    //                 });

    //             if (conditions.length > 0)
    //                 return QueryFilterGroup.JoinWithAndOperator(conditions);
    //             return undefined;

    //             // if (filterMetaData.operator == undefined) return conditions[0];

    //             // switch (filterMetaData) {
    //             //   case 'AND':
    //             //     return QueryFilterGroup.JoinWithAndOperator(conditions);
    //             //   default:
    //             //     return QueryFilterGroup.JoinWithOrOperator(conditions);
    //             // }
    //         }
    //     );

    //     const validFitlers = filters.filter((filter) => filter != undefined);
    //     if (validFitlers.length == 0) return undefined;

    //     const filterGroup = QueryFilterGroup.JoinWithAndOperator(validFitlers);
    //     // const filterGroup = QueryFilterGroup.JoinWithOrOperator([
    //     //   QueryFilterGroup.FieldIsEqualToString<SupplierType>(
    //     //     ['Code'],
    //     //     'searchToken',
    //     //   ),
    //     // ]);

    //     // Samples of AGGrid Filters
    //     const tempSimpleFitler = {
    //         BrandName: {
    //             filterType: 'text',
    //             type: 'contains',
    //             filter: 'al'
    //         } as ZAGGridFilterCondition,
    //         TaxId: {
    //             filterType: 'text',
    //             type: 'contains',
    //             filter: '09'
    //         } as ZAGGridFilterCondition
    //     };
    //     const tempFitler: ZAGGridFilters = {
    //         BrandName: {
    //             filterType: 'text',
    //             operator: 'AND',
    //             conditions: [
    //                 { filterType: 'text', type: 'contains', filter: 'ae' },
    //                 { filterType: 'text', type: 'contains', filter: 'eb' }
    //             ]
    //         },
    //         'OccupationActivity.Title': {
    //             filterType: 'text',
    //             operator: 'AND',
    //             conditions: [
    //                 { filterType: 'text', type: 'contains', filter: 'f' },
    //                 { filterType: 'text', type: 'contains', filter: 'a' }
    //             ]
    //         },
    //         TaxId: {
    //             filterType: 'text',
    //             operator: 'AND',
    //             conditions: [
    //                 { filterType: 'text', type: 'contains', filter: '09' },
    //                 { filterType: 'text', type: 'contains', filter: '63' }
    //             ]
    //         },
    //         StartDate: {
    //             filterType: 'date',
    //             operator: 'AND',
    //             conditions: [
    //                 {
    //                     dateFrom: '2025-01-28 00:00:00',
    //                     dateTo: '2025-02-28 00:00:00',
    //                     filterType: 'date',
    //                     type: 'inRange'
    //                 },
    //                 {
    //                     dateFrom: '2025-02-05 00:00:00',
    //                     dateTo: '2025-03-01 00:00:00',
    //                     filterType: 'date',
    //                     type: 'inRange'
    //                 }
    //             ]
    //         }
    //     };

    //     return filterGroup;
    // }

    onSelectedItemChange(event: {
        menuItem: MenuItem | undefined;
        itemObj: DigitalClientCardType;
    }) {
        //this.selectedDCLCard = event.itemObj;
        this.newInvoice(event.itemObj, event.menuItem?.['code']);
    }

    // onDropdownClick(event: MouseEvent, dclCard: DigitalClientCardType) {
    //     this.selectedDCLCard = dclCard;
    // }
    correlate(dclCard: DigitalClientCardType, invoice: InvoiceType) {
        this.confirmationService.confirm({
            //target: event.target as EventTarget,
            message: 'Θέλετε να Συσχετίσετε την εγγραφή με το Παραστατικό?',
            // header: 'Επιβεβαίωση',
            defaultFocus: 'reject',
            closable: false,
            closeOnEscape: true,
            icon: 'pi pi-question',
            rejectButtonProps: {
                label: 'Ακύρωση',
                severity: 'secondary',
                outlined: true
            },
            acceptButtonProps: {
                label: 'Συσχέτιση',
                outlined: true
            },
            accept: () => {
                this.loaderService.showAwaitLoader();
                this.dclCardService
                    .correlateDigitalClientCard(dclCard, invoice, undefined)
                    .pipe(
                        finalize(() => {
                            this.loaderService.dismissAwaitLoader();
                            this.refreshTable();
                        })
                    )
                    .subscribe();
            },
            reject: () => {
                // this.messageService.add({
                //     severity: 'error',
                //     summary: 'Rejected',
                //     detail: 'You have rejected',
                //     life: 3000,
                // });
            }
        });
    }

    /**
     * @deprecated
     * @param dclCard
     * @param invoiceTypeCode
     */
    newInvoice(dclCard: DigitalClientCardType, invoiceTypeCode: string) {
        const dialogRef = this.dialogService.open<InvoiceCardComponent>(
            InvoiceCardComponent,
            {
                header: 'Νέο Παραστατικό',
                height: '95vh',
                width: '95vw',
                modal: true,
                resizable: true,
                draggable: true,
                closeOnEscape: false,
                closable: true,
                focusOnShow: false,
                maximizable: true,

                templates: {
                    footer: InvoiceCardFooterComponent
                },
                data: {
                    mode: 'add',
                    dclId: dclCard?._id,
                    dclCard: dclCard,
                    DCLInvoiceTypeCode: invoiceTypeCode,
                    onSave: (options: InvoiceOptions) => {
                        console.debug(
                            'dcl-cards.componnt - newInvoice - onSave callback',
                            options
                        );
                        dialogRef.close();
                        //if (this.event) this.onLazyLoad(this.event);
                    },
                    onSentToAADE: (
                        options: InvoiceOptions,
                        invoice: InvoiceType
                    ) => {
                        dialogRef.close();
                        //if (this.event) this.onLazyLoad(this.event);
                    }
                }
            } as DynamicDialogConfig<InvoiceOptions>
        );

        dialogRef.onClose?.pipe(takeUntil(this.unSubscribe)).subscribe(() => {
            this.refreshTable();
        });
    }

    /**
     * @deprecated
     * μεταβολη παραστατικού από το grid των dscl cards
     * @param dclCard
     * @param index
     */
    editInvoice(dclCard: DigitalClientCardType, index: number) {
        let invoiceCardComponent: InvoiceCardComponent | undefined = undefined;
        const dialogRef = this.dialogService.open<InvoiceCardComponent>(
            InvoiceCardComponent,
            {
                header: 'Παραστατικό',
                height: '95vh',
                width: '95vw',
                modal: true,
                resizable: true,
                draggable: true,
                closeOnEscape: false,
                closable: true,
                focusOnShow: false,
                maximizable: true,

                templates: {
                    footer: InvoiceCardFooterComponent
                },
                data: {
                    dclId: dclCard?._id,
                    dclCard: dclCard,
                    DCLInvoiceTypeCode: dclCard.Invoices?.[index]?.TypeCode,
                    invoiceType: dclCard.Invoices?.[index],
                    mode: 'edit',
                    onSave: (options: InvoiceOptions) => {
                        dialogRef.close();
                        //if (this.event) this.onLazyLoad(this.event);
                    },
                    onRefreshDCLCards: () => {
                        dialogRef.close();
                    },
                    onSentToAADE: (
                        options: InvoiceOptions,
                        invoice: InvoiceType
                    ) => {
                        dialogRef.close();
                        //if (this.event) this.onLazyLoad(this.event);
                    }
                }
            } as DynamicDialogConfig<InvoiceOptions>
        );

        dialogRef.onChildComponentLoaded.subscribe(
            (invoiceCard: InvoiceCardComponent) => {
                console.debug('child component loaded ', invoiceCard);
                invoiceCardComponent = invoiceCard;
                debugger;
            }
        );

        dialogRef.onClose?.pipe(takeUntil(this.unSubscribe)).subscribe(() => {
            this.refreshTable();
        });

        dialogRef.onMaximize
            ?.pipe(takeUntil(this.unSubscribe))
            .subscribe((value) => {
                // if (invoiceCardComponent)
                //     invoiceCardComponent.dialogHeight.set(value);

                console.debug('on Maximize', value);
            });
        dialogRef.onResizeEnd
            ?.pipe(takeUntil(this.unSubscribe))
            .subscribe((value) => {
                // if (invoiceCardComponent)
                //     invoiceCardComponent.dialogHeight.set('580px');

                console.debug('onResizeEnd ', value);
            });
    }

    ngOnDestroy() {
        this.unSubscribe.next(true);
        this.unSubscribe.complete();
    }
}
